import { useMemo, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import {
  FormAutocomplete,
  FeedbackSnackbar,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  ValueBadge,
  type ResourceCollectionState,
} from '../../../design-system';
import { RewardsCreateAction } from '../RewardsCreateAction';
import { fixtureRewardsSnapshot, fixtureRewardWorkers } from '../fixtures';
import {
  rewardStatusLabel,
  rewardStatusTone,
  rewardsRows,
  rewardsReadError,
  type RewardsApi,
  type RewardsRow,
  type RewardWorkerOption,
} from '../model';
import { useAsyncResource } from '../../../shared/useAsyncResource';

export type RewardsPageProps = {
  api: RewardsApi;
  initialWorkerId: string;
  initialKind?: string;
  fixtureMode: boolean;
  canCreate: boolean;
  canSearchWorkers: boolean;
};

function collectionState({
  loading,
  error,
  rowCount,
  onRetry,
}: {
  loading: boolean;
  error?: Error;
  rowCount: number;
  onRetry: () => void;
}): ResourceCollectionState {
  if (loading) return { kind: 'loading' };
  if (error) return { kind: 'error', message: rewardsReadError(error), onRetry };
  if (rowCount === 0) return { kind: 'empty', title: '조건에 맞는 보상 정보가 없습니다.' };
  return { kind: 'ready' };
}

function rewardKindLabel(value: string) {
  return ({ CONTRACT: '계약', COMPENSATION: '보상', BENEFIT: '복리후생' } as Record<string, string>)[value] ?? value;
}

function RewardsStatus({ value }: { value: string }) {
  return <StatusBadge tone={rewardStatusTone(value)} label={rewardStatusLabel(value)} />;
}

export function RewardsPage({
  api,
  initialWorkerId,
  initialKind,
  fixtureMode,
  canCreate,
  canSearchWorkers,
}: RewardsPageProps) {
  const [workerId, setWorkerId] = useState(initialWorkerId);
  const [kind, setKind] = useState(initialKind && ['CONTRACT', 'COMPENSATION', 'BENEFIT'].includes(initialKind) ? initialKind : '');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');

  const workersResource = useAsyncResource<RewardWorkerOption[]>(async () => {
    if (fixtureMode) return fixtureRewardWorkers;
    if (!canSearchWorkers) return [];
    return api.listAllWorkers();
  }, [api, canSearchWorkers, fixtureMode]);

  const resource = useAsyncResource(async () => {
    if (fixtureMode) return fixtureRewardsSnapshot;
    if (!workerId) return undefined;
    return api.getWorkerRewards(workerId);
  }, [api, fixtureMode, workerId]);

  const rows = useMemo(
    () => (resource.data ? rewardsRows(resource.data) : []).filter((row) => (
      (!kind || row.kind === kind)
      && `${row.descriptor} ${row.detail}`.toLowerCase().includes(query.toLowerCase())
    )),
    [resource.data, kind, query],
  );
  const workerOptions = [...(workersResource.data ?? [])];
  const selectedWorker = workerOptions.find((option) => option.id === workerId) ?? workerOptions.find((option) => option.id === resource.data?.worker?.id);
  if (workerId && !selectedWorker) workerOptions.unshift({ id: workerId, label: resource.data?.worker?.descriptor ?? (workerId === initialWorkerId ? '현재 로그인한 구성원' : '선택한 구성원') });
  const columns: GridColDef<RewardsRow>[] = [
    { field: 'kind', headerName: '구분', width: 120, valueFormatter: (value) => rewardKindLabel(value), renderCell: ({ value }) => <ValueBadge label={rewardKindLabel(value)} /> },
    { field: 'descriptor', headerName: '항목', flex: 1, minWidth: 220, renderCell: ({ row }) => row.kind === 'BENEFIT' ? row.descriptor : <ValueBadge label={row.descriptor} /> },
    { field: 'detail', headerName: '현재 값', width: 220, sortable: false, renderCell: ({ row }) => row.kind === 'BENEFIT' && row.detail ? <ValueBadge label={row.detail} /> : row.detail },
    { field: 'period', headerName: '효력 시작', width: 130 },
    { field: 'status', headerName: '상태', width: 120, renderCell: ({ value }) => <RewardsStatus value={value} /> },
  ];

  return <>
    <RewardsCreateAction
      api={api}
      fixtureMode={fixtureMode}
      onCreated={(targetWorkerId) => {
        setNotice('보상 변경을 승인 요청했습니다. 승인 전까지 현재 원장은 바뀌지 않습니다.');
        if (targetWorkerId === workerId) resource.reload();
        else setWorkerId(targetWorkerId);
      }}
      initialWorkerId={resource.data?.worker?.id ?? workerId}
      canSearchWorkers={canSearchWorkers}
      canCreate={canCreate}
    >
      {(primaryAction) => (
        <ResourcePage
          title="보상·계약·복리후생"
          primaryAction={primaryAction}
        >
          <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice('')} />
            {canSearchWorkers && <Box sx={{ mb: 2, maxWidth: 480 }}>
              <FormAutocomplete options={workerOptions} value={selectedWorker ?? workerOptions.find((item) => item.id === workerId) ?? null}
                loading={workersResource.loading} getOptionLabel={(option) => option.label} isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, option) => { setWorkerId(option?.id ?? ''); }} input={{ label: '조회할 구성원', placeholder: '이름으로 구성원 선택' }} />
              {workersResource.error && <Alert severity="error" action={<Button onClick={workersResource.reload}>다시 시도</Button>}>구성원 목록을 불러오지 못했습니다.</Alert>}
            </Box>}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResourceCollection
                state={!workerId ? { kind: 'empty', title: '조회할 구성원을 선택해 주세요.' } : collectionState({ loading: resource.loading, error: resource.error, rowCount: rows.length, onRetry: resource.reload })}
                rows={rows}
                totalCount={resource.data ? rewardsRows(resource.data).length : 0}
                columns={columns}
                query={query}
                onQueryChange={setQuery}
                searchLabel="보상 정보 검색"
                searchPlaceholder="계약·보상·복리후생 검색"
                ariaLabel="보상·계약·복리후생 목록"
                filters={[
                  {
                    id: 'kind',
                    label: '구분',
                    value: kind,
                    defaultValue: '',
                    options: [
                      { label: '전체', value: '' },
                      { label: '계약', value: 'CONTRACT' },
                      { label: '보상', value: 'COMPENSATION' },
                      { label: '복리후생', value: 'BENEFIT' },
                    ],
                    onChange: setKind,
                  },
                ]}
              />
            </Box>
          </Box>
        </ResourcePage>
      )}
    </RewardsCreateAction>
  </>;
}
